import { RubyGemMark } from '../components/RubyGemMark';
import { RubyLoader } from '../components/RubyLoader';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Percent, Plus, Trash2, AlertTriangle, Check, X, Star, Copy, Search,
  ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Pencil, Package, Cpu,
} from 'lucide-react';
import { billingApi, type CommissionPlanRow } from '../services/billingApi';
import SearchableDropdown from '../components/SearchableDropdown';
import { money } from '../components/finance/financeFormat';
import { InventoryPage, RUBY, RUBY_DEEP } from './inventory/InventoryChrome';

/**
 * COMMISSION PLANS — the LIST (`/commission-plans`).
 *
 * Rates are edited at `/commission-plans/:planId`; this screen only finds a
 * plan and says enough about it to pick the right one.
 *
 * WHY A TABLE AND A SEPARATE EDITOR. This used to be a 320px sidebar of cards
 * beside an inline editor. That works at five plans and fails at fifty: the
 * cards carry no comparable columns, so "which plan pays the most on
 * hardware?" means opening every one, and the editor — which has a
 * full-width target picker per rule — was permanently squeezed into the
 * remaining space. Splitting them gives the list real columns (sortable,
 * filterable, paged) and gives the editor the whole width it needs.
 *
 * The list is filtered and sorted CLIENT-SIDE on purpose: `listPlans` returns
 * every plan in one scan (they number in the tens, not the thousands), so
 * paging here is about readability, not about avoiding a fetch. If plans ever
 * reach a size where that stops being true, this is the screen that changes —
 * the editor is already addressed by id and needs nothing.
 */

type SortKey = 'name' | 'rules' | 'version' | 'updated' | 'status';
type StatusFilter = 'all' | 'active' | 'draft' | 'retired';
type DefaultFilter = 'all' | 'default' | 'not_default';

const PAGE_SIZES = [10, 20, 50, 100];

const STATUS_CHIP: Record<string, string> = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  draft: 'bg-stone-100 text-stone-600 border-stone-200',
  retired: 'bg-amber-50 text-amber-700 border-amber-200',
};

const dateStr = (ms?: number) => (ms
  ? new Date(ms).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
  : '—');

/**
 * What a plan pays, in one cell.
 *
 * Deliberately a COUNT plus the headline rate per scope rather than every
 * rule: the point of the column is to tell two plans apart at a glance, and a
 * cell that lists eight rules tells you nothing faster than opening the plan
 * does. The exact rates live one click away.
 */
const scopeSummary = (plan: CommissionPlanRow, scope: string): string | null => {
  const rules = (plan.rules || []).filter((r) => r.scope === scope);
  if (!rules.length) return null;
  const head = rules[0];
  const rate = head.basis === 'flat_per_unit'
    ? `${money(head.flatCents || 0)}/unit`
    : `${head.rate || 0}%${head.basis === 'percent_of_margin' ? ' margin' : ''}`;
  return rules.length === 1 ? rate : `${rate} +${rules.length - 1}`;
};

const CommissionPlansPage: React.FC = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<CommissionPlanRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  /** Matches name OR planId OR any rule target — the plan id is what an
   *  operator pastes in from a payment investigation, so it must be findable. */
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [defaultOnly, setDefaultOnly] = useState<DefaultFilter>('all');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortAsc, setSortAsc] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await billingApi.listCommissionPlans();
      setPlans(res.items || []);
      if (res.invalid?.length) {
        // Surfaced loudly rather than hidden: a plan nobody can parse is a plan
        // that pays nobody, and silence would make that look like "no earnings".
        setError(`${res.invalid.length} plan row(s) could not be read and will pay nothing: ${res.invalid.join(', ')}`);
      }
    } catch (e: any) {
      setError(e?.message || 'Could not load commission plans');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /** Any filter change returns to page 1 — otherwise a narrowed list can land
   *  on a page that no longer exists and read as "no plans". */
  useEffect(() => { setPage(1); }, [query, status, defaultOnly, pageSize]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return plans.filter((p) => {
      if (status !== 'all' && p.status !== status) return false;
      if (defaultOnly === 'default' && !p.isDefault) return false;
      if (defaultOnly === 'not_default' && p.isDefault) return false;
      if (!q) return true;
      return p.name.toLowerCase().includes(q)
        || p.planId.toLowerCase().includes(q)
        || (p.description || '').toLowerCase().includes(q)
        || (p.rules || []).some((r) => r.target.toLowerCase().includes(q)
          || (r.targets || []).some((t) => t.toLowerCase().includes(q)));
    });
  }, [plans, query, status, defaultOnly]);

  const sorted = useMemo(() => {
    const dir = sortAsc ? 1 : -1;
    const rows = [...filtered];
    rows.sort((a, b) => {
      switch (sortKey) {
        case 'rules': return dir * ((a.rules?.length || 0) - (b.rules?.length || 0));
        case 'version': return dir * (a.version - b.version);
        case 'updated': return dir * ((a.updatedAt || 0) - (b.updatedAt || 0));
        case 'status': return dir * a.status.localeCompare(b.status);
        default: return dir * a.name.localeCompare(b.name);
      }
    });
    // The global default always floats to the top, whatever the sort: it is
    // the plan that pays when a deal names none, so it is never buried on
    // page 3 of an alphabetical list.
    rows.sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
    return rows;
  }, [filtered, sortKey, sortAsc]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  const current = Math.min(page, pageCount);
  const rows = sorted.slice((current - 1) * pageSize, current * pageSize);

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) setSortAsc((v) => !v);
    else { setSortKey(key); setSortAsc(true); }
  };

  /** Clipboard can be blocked (insecure origin, permissions) — never throw. */
  const copyId = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      setNotice(`Copied ${id}`);
    } catch {
      setError(`Could not copy automatically — the id is ${id}`);
    }
  };

  const remove = async (plan: CommissionPlanRow) => {
    setBusyId(plan.planId);
    setError('');
    try {
      await billingApi.deleteCommissionPlan(plan.planId);
      setNotice(`Deleted "${plan.name}"`);
      await load();
    } catch (e: any) {
      // The server's refusal (deleting the default) is shown verbatim.
      setError(e?.message || 'Could not delete the plan');
    } finally {
      setBusyId(null);
    }
  };

  const SortHead: React.FC<{ k: SortKey; children: React.ReactNode; className?: string }> = ({
    k, children, className = '',
  }) => (
    <th className={`px-3 py-2 text-left ${className}`}>
      <button
        onClick={() => toggleSort(k)}
        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-stone-500 hover:text-stone-800"
      >
        {children}
        {sortKey === k && (sortAsc ? <ChevronUp size={11} /> : <ChevronDown size={11} />)}
      </button>
    </th>
  );

  return (
    <InventoryPage
      icon={Percent}
      title="Commission Plans"
      subtitle="Reusable partner commission rule sets. A deal points at one; a partner can be pinned to another."
      actions={(
        <button
          onClick={() => navigate('/commission-plans/new')}
          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold text-white"
          style={{ background: `linear-gradient(135deg, ${RUBY}, ${RUBY_DEEP})` }}
        >
          <Plus size={14} /> New plan
        </button>
      )}
    >
      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-700">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="shrink-0 text-red-400 hover:text-red-700"><X size={13} /></button>
        </div>
      )}
      {notice && (
        <div className="mb-4 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-700">
          <Check size={15} /> <span className="flex-1">{notice}</span>
          <button onClick={() => setNotice('')} className="text-emerald-500 hover:text-emerald-800"><X size={13} /></button>
        </div>
      )}

      {/* ---- toolbar --------------------------------------------------- */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[240px] flex-1">
          <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, plan id, description or a rule's target…"
            className="w-full rounded-lg border border-stone-300 bg-white py-2.5 pl-9 pr-3 text-xs outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
          />
        </div>
        <div className="w-44">
          <SearchableDropdown
            options={[
              { value: 'all', label: 'Any status' },
              { value: 'active', label: 'Active', subtitle: 'Live — these decide real payouts' },
              { value: 'draft', label: 'Draft', subtitle: 'Not paying anyone yet' },
              { value: 'retired', label: 'Retired', subtitle: 'Hidden from pickers' },
            ]}
            value={status}
            onChange={(v) => setStatus((v as StatusFilter) || 'all')}
            placeholder="Status"
            variant="light"
            compact
          />
        </div>
        <div className="w-44">
          <SearchableDropdown
            options={[
              { value: 'all', label: 'Default & others' },
              { value: 'default', label: 'The global default', subtitle: 'What pays when a deal names no plan' },
              { value: 'not_default', label: 'Everything else' },
            ]}
            value={defaultOnly}
            onChange={(v) => setDefaultOnly((v as DefaultFilter) || 'all')}
            placeholder="Default"
            variant="light"
            compact
          />
        </div>
        <span className="text-[11px] text-stone-500">
          {sorted.length} of {plans.length} plan{plans.length === 1 ? '' : 's'}
        </span>
      </div>

      {loading ? (
        <RubyLoader variant="section" label="Loading plans…" className="rounded-xl border border-stone-200 bg-white" />
      ) : plans.length === 0 ? (
        <div className="rounded-xl border border-dashed border-stone-300 bg-white p-10 text-center text-sm text-stone-500">
          No commission plans yet. Create one — until a plan exists, nothing a partner sells can pay them.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
          {/* Wide table scrolls INSIDE its own container — the page body never
              scrolls sideways. */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] border-collapse text-sm">
              <thead className="border-b border-stone-200 bg-stone-50">
                <tr>
                  <SortHead k="name">Plan</SortHead>
                  <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-stone-500">Plan id</th>
                  <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-stone-500">Pays</th>
                  <SortHead k="rules" className="text-right">Rules</SortHead>
                  <SortHead k="version">Ver</SortHead>
                  <SortHead k="status">Status</SortHead>
                  <SortHead k="updated">Updated</SortHead>
                  <th className="w-20 px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-xs text-stone-400">
                      Nothing matches these filters.
                    </td>
                  </tr>
                )}
                {rows.map((p) => {
                  const software = scopeSummary(p, 'software_package');
                  const hardware = scopeSummary(p, 'device_offer');
                  return (
                    <tr
                      key={p.planId}
                      onClick={() => navigate(`/commission-plans/${encodeURIComponent(p.planId)}`)}
                      className="cursor-pointer border-b border-stone-100 transition last:border-0 hover:bg-rose-50/40"
                    >
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-stone-900">{p.name}</span>
                          {p.isDefault && (
                            <span
                              className="inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[9px] font-bold"
                              style={{ background: 'rgba(220,20,60,.12)', color: RUBY_DEEP }}
                            >
                              <Star size={9} /> DEFAULT
                            </span>
                          )}
                        </div>
                        {p.description && (
                          <div className="mt-0.5 max-w-[320px] truncate text-[11px] text-stone-500">{p.description}</div>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <button
                          onClick={(e) => { e.stopPropagation(); copyId(p.planId); }}
                          title="Copy plan id — every commission event records this"
                          className="inline-flex max-w-[220px] items-center gap-1 rounded border border-stone-200 bg-stone-50 px-1.5 py-0.5 font-mono text-[10.5px] text-stone-600 hover:border-rose-300 hover:text-rose-600"
                        >
                          <span className="truncate">{p.planId}</span>
                          <Copy size={9} className="shrink-0 opacity-60" />
                        </button>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex flex-wrap items-center gap-1.5">
                          {software && (
                            <span className="inline-flex items-center gap-1 rounded-md bg-indigo-50 px-1.5 py-0.5 text-[10.5px] font-medium text-indigo-700">
                              <Package size={10} /> {software}
                            </span>
                          )}
                          {hardware && (
                            <span className="inline-flex items-center gap-1 rounded-md bg-sky-50 px-1.5 py-0.5 text-[10.5px] font-medium text-sky-700">
                              <Cpu size={10} /> {hardware}
                            </span>
                          )}
                          {!software && !hardware && (
                            // A plan with no rules pays nothing at all. Said in
                            // the list rather than discovered after assigning it.
                            <span className="text-[10.5px] text-amber-600">no rules — pays nothing</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono text-xs text-stone-600">{p.rules?.length || 0}</td>
                      <td className="px-3 py-2.5 font-mono text-xs text-stone-500">v{p.version}</td>
                      <td className="px-3 py-2.5">
                        <span className={`inline-block rounded-md border px-1.5 py-0.5 text-[10px] font-semibold capitalize ${STATUS_CHIP[p.status] || STATUS_CHIP.draft}`}>
                          {p.status}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-stone-500">{dateStr(p.updatedAt)}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/commission-plans/${encodeURIComponent(p.planId)}`); }}
                            title="Edit"
                            className="rounded-lg border border-stone-200 p-1.5 text-stone-500 hover:border-rose-300 hover:text-rose-600"
                          >
                            <Pencil size={13} />
                          </button>
                          {/* The default plan cannot be deleted — the server
                              refuses it, so the control is not offered either. */}
                          {!p.isDefault && (
                            <button
                              onClick={(e) => { e.stopPropagation(); remove(p); }}
                              disabled={busyId === p.planId}
                              title="Delete"
                              className="rounded-lg border border-stone-200 p-1.5 text-stone-400 hover:border-red-300 hover:text-red-500 disabled:opacity-40"
                            >
                              {busyId === p.planId ? <RubyGemMark size={13} drawing color="currentColor" /> : <Trash2 size={13} />}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ---- pagination ---------------------------------------------- */}
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-stone-200 bg-stone-50 px-3 py-2">
            <span className="text-[11px] text-stone-500">
              {sorted.length === 0
                ? 'No plans'
                : `${(current - 1) * pageSize + 1}–${Math.min(current * pageSize, sorted.length)} of ${sorted.length}`}
            </span>
            <div className="flex items-center gap-2">
              <div className="w-28">
                <SearchableDropdown
                  options={PAGE_SIZES.map((n) => ({ value: String(n), label: `${n} per page` }))}
                  value={String(pageSize)}
                  onChange={(v) => setPageSize(Number(v) || 20)}
                  placeholder="Rows"
                  variant="light"
                  compact
                />
              </div>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={current <= 1}
                className="rounded-lg border border-stone-300 bg-white p-1.5 text-stone-600 disabled:opacity-40"
              >
                <ChevronLeft size={14} />
              </button>
              <span className="font-mono text-[11px] text-stone-600">{current} / {pageCount}</span>
              <button
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                disabled={current >= pageCount}
                className="rounded-lg border border-stone-300 bg-white p-1.5 text-stone-600 disabled:opacity-40"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      <p className="mt-3 text-[10px] text-stone-400">
        <strong>The plan id is the tracking key.</strong> Every commission event records the planId, the
        plan version and the exact rule that priced it, so any payment can be traced back to the rates that
        produced it — even after those rates change. Ids are minted from the plan name, are never reused and
        never change once a plan exists.
      </p>
    </InventoryPage>
  );
};

export default CommissionPlansPage;
